import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Grid, CircularProgress, Typography, Autocomplete } from "@mui/material";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { getClient, updateClient, activateClient, deactivateClient } from "../redux/ducks/client";
import { getIntuitCustomers } from "../redux/ducks/invoices";
import { getManagers } from "../redux/slices/users";
import { useHasPermission } from "../utils/permissionUtils";

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
}));

const FormStack = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    paddingTop: theme.spacing(1),
}));

const Section = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const EditBillingClientDialog = ({ open, clientId, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canEditClient = hasPermission("editClient");
    const canEditBillingTerms = hasPermission("editBillingTerms");
    const canEditDetails = canEditClient;
    const canEditTerms = canEditClient && canEditBillingTerms;
    const canChangeStatus = hasPermission("activateClient") && hasPermission("deactivateClient");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [client, setClient] = useState(null);
    const [users, setUsers] = useState([]);
    const [managers, setManagers] = useState([]);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [website, setWebsite] = useState("");
    const [active, setActive] = useState(true);
    const [address, setAddress] = useState({});
    const [billingTerms, setBillingTerms] = useState({});
    const [clientManager, setClientManager] = useState("");
    const [primaryContact, setPrimaryContact] = useState("");
    const [billingContact, setBillingContact] = useState("");
    const [emailError, setEmailError] = useState("");
    const [intuitCustomerId, setIntuitCustomerId] = useState("");
    const [intuitCustomers, setIntuitCustomers] = useState([]);
    const [intuitConnected, setIntuitConnected] = useState(false);
    const [loadingIntuitCustomers, setLoadingIntuitCustomers] = useState(false);
    const [intuitCustomerTouched, setIntuitCustomerTouched] = useState(false);

    useEffect(() => {
        if (!open || !clientId) return;
        setLoading(true);
        dispatch(
            getClient(clientId, (data) => {
                setLoading(false);
                if (!data) return;
                setClient(data);
                const clientUsers = (data.users || []).filter((u) => u.active && !u.isDeleted);
                setUsers(clientUsers);
                setName(data.name || "");
                setEmail(data.email || "");
                setPhone(data.phone || "");
                setWebsite(data.website || "");
                setActive(data.active !== false);
                setAddress(data.address || {});
                setBillingTerms(data.billingTerms || {});
                setClientManager(data.clientManager?._id || data.clientManager || "");
                setPrimaryContact(data.primaryContact?._id || data.primaryContact || "");
                setBillingContact(data.billingContact || "");
                setIntuitCustomerId(data.intuitCustomerId || "");
                setIntuitCustomerTouched(false);
                setEmailError("");
            }),
        );
        dispatch(
            getManagers({
                callback: (data) => {
                    setManagers(Array.isArray(data) ? data : []);
                },
            }),
        );
        setLoadingIntuitCustomers(true);
        dispatch(
            getIntuitCustomers((data) => {
                setLoadingIntuitCustomers(false);
                setIntuitConnected(Boolean(data?.connected));
                setIntuitCustomers(Array.isArray(data?.customers) ? data.customers : []);
            }),
        );
    }, [open, clientId, dispatch]);

    const validateEmail = (value) => {
        if (!value || value.trim() === "") return "";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? "" : "Invalid email format";
    };

    const CREATE_NEW_OPTION = { id: "", displayName: "Create a new customer in QuickBooks", email: null };
    const intuitCustomerOptions = [CREATE_NEW_OPTION, ...intuitCustomers];

    // Punctuation is ignored so "Vessel Brand, Inc." still finds "Vessel Brand Inc".
    const normalizeName = (value) =>
        String(value || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim();
    const linkedIntuitCustomer = intuitCustomers.find((c) => c.id === intuitCustomerId);
    const suggestedIntuitCustomer =
        !intuitCustomerId && !intuitCustomerTouched && name
            ? intuitCustomers.find((c) => normalizeName(c.displayName) === normalizeName(name))
            : null;
    const selectedIntuitCustomer = linkedIntuitCustomer || suggestedIntuitCustomer || CREATE_NEW_OPTION;

    const updateAddress = (field, value) => setAddress((prev) => ({ ...prev, [field]: value }));
    const updateBillingTerms = (field, value) => setBillingTerms((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => {
        const err = validateEmail(billingContact);
        if (err) {
            setEmailError(err);
            return;
        }
        setSaving(true);
        const updates = {
            name,
            email: email?.trim() || "",
            phone,
            website,
            address: {
                street: address.street || "",
                apartment: address.apartment || "",
                city: address.city || "",
                state: address.state || "",
                postalCode: address.postalCode || "",
                country: address.country || "",
            },
            billingTerms,
            clientManager: clientManager || null,
            primaryContact: primaryContact || null,
            billingContact: billingContact?.trim() || "",
            // Persist what the field shows, so accepting a suggested match is just Save.
            intuitCustomerId: selectedIntuitCustomer.id || null,
        };
        dispatch(
            updateClient(clientId, updates, (resp) => {
                if (!resp) {
                    setSaving(false);
                    return;
                }
                if (canChangeStatus) {
                    const wasActive = client?.active !== false;
                    if (active !== wasActive) {
                        dispatch(active ? activateClient(clientId, () => {}) : deactivateClient(clientId, () => {}));
                    }
                }
                setSaving(false);
                if (onSaved) onSaved();
                onClose();
            }),
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit Client{client?.name ? ` — ${client.name}` : ""}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <CenteredLoading>
                        <CircularProgress />
                    </CenteredLoading>
                ) : (
                    <FormStack>
                        <Typography variant="body2" color="text.secondary">
                            Update this client's contact details, address, status, and billing settings.
                        </Typography>

                        {canEditDetails && (
                            <Section>
                                <SectionTitle variant="subtitle2">Client details</SectionTitle>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Email"
                                            type="email"
                                            fullWidth
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            helperText="General contact email for the client"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Phone" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Website" fullWidth value={website} onChange={(e) => setWebsite(e.target.value)} />
                                    </Grid>
                                </Grid>
                            </Section>
                        )}

                        {canChangeStatus && (
                            <Section>
                                <SectionTitle variant="subtitle2">Status</SectionTitle>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Status"
                                            select
                                            fullWidth
                                            value={active}
                                            onChange={(e) => setActive(e.target.value === "true" || e.target.value === true)}
                                            helperText="Inactive clients are hidden from active lists"
                                        >
                                            <MenuItem value={true}>Active</MenuItem>
                                            <MenuItem value={false}>Inactive</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Section>
                        )}

                        {canEditDetails && (
                            <Section>
                                <SectionTitle variant="subtitle2">Address</SectionTitle>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Street" fullWidth value={address.street || ""} onChange={(e) => updateAddress("street", e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Apartment" fullWidth value={address.apartment || ""} onChange={(e) => updateAddress("apartment", e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="City" fullWidth value={address.city || ""} onChange={(e) => updateAddress("city", e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="State" fullWidth value={address.state || ""} onChange={(e) => updateAddress("state", e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Postal Code" fullWidth value={address.postalCode || ""} onChange={(e) => updateAddress("postalCode", e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Country" fullWidth value={address.country || ""} onChange={(e) => updateAddress("country", e.target.value)} />
                                    </Grid>
                                </Grid>
                            </Section>
                        )}

                        <Section>
                            <SectionTitle variant="subtitle2">Billing settings</SectionTitle>
                            <TextField
                                label="Client Relationship Manager"
                                select
                                fullWidth
                                value={clientManager || ""}
                                onChange={(e) => setClientManager(e.target.value || "")}
                                helperText="Internal user who owns this client relationship"
                            >
                                <MenuItem value="">None</MenuItem>
                                {managers.map((m) => (
                                    <MenuItem key={m._id} value={m._id}>
                                        {m.fullName || `${m.first_name || ""} ${m.last_name || ""}`.trim()} {m.email ? `(${m.email})` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Primary Contact"
                                select
                                fullWidth
                                value={primaryContact || ""}
                                onChange={(e) => setPrimaryContact(e.target.value || "")}
                                helperText="A user that belongs to this client"
                            >
                                <MenuItem value="">None</MenuItem>
                                {users.map((u) => (
                                    <MenuItem key={u._id} value={u._id}>
                                        {u.fullName} ({u.email})
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Billing Contact Email"
                                type="email"
                                fullWidth
                                value={billingContact}
                                onChange={(e) => {
                                    setBillingContact(e.target.value);
                                    if (emailError) setEmailError("");
                                }}
                                onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                                error={Boolean(emailError)}
                                helperText={emailError || "Invoices will be emailed to this address with the PDF attached"}
                            />
                        </Section>

                        {intuitConnected && (
                            <Section>
                                <SectionTitle variant="subtitle2">QuickBooks</SectionTitle>
                                <Autocomplete
                                    options={intuitCustomerOptions}
                                    loading={loadingIntuitCustomers}
                                    value={selectedIntuitCustomer}
                                    onChange={(e, option) => {
                                        setIntuitCustomerTouched(true);
                                        setIntuitCustomerId(option?.id || "");
                                    }}
                                    getOptionLabel={(option) => option.displayName || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id || "create-new"}>
                                            <Box>
                                                <Typography variant="body2">{option.displayName}</Typography>
                                                {option.email && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="QuickBooks Customer"
                                            helperText={
                                                suggestedIntuitCustomer
                                                    ? "Matched by name — save to link this client to it, or pick a different customer"
                                                    : selectedIntuitCustomer.id
                                                      ? "This client's invoices will be filed under this QuickBooks customer"
                                                      : "No customer chosen — a new one will be created in QuickBooks from the client name on the first invoice"
                                            }
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingIntuitCustomers ? <CircularProgress size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Section>
                        )}

                        {canEditTerms && (
                            <Section>
                                <SectionTitle variant="subtitle2">Billing terms</SectionTitle>
                                <TextField label="Billing Type" select fullWidth value={billingTerms.type || ""} onChange={(e) => updateBillingTerms("type", e.target.value)}>
                                    <MenuItem value="Hourly">Hourly</MenuItem>
                                    <MenuItem value="Retainer">Retainer</MenuItem>
                                    <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                                    <MenuItem value="Internal">Internal</MenuItem>
                                    <MenuItem value="Non Billable">Non Billable</MenuItem>
                                </TextField>
                                {billingTerms.type === "Retainer" && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="Hours"
                                                type="number"
                                                fullWidth
                                                value={billingTerms.hours ?? ""}
                                                onChange={(e) => updateBillingTerms("hours", e.target.value === "" ? "" : Number(e.target.value))}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="Frequency"
                                                select
                                                fullWidth
                                                value={billingTerms.frequency || ""}
                                                onChange={(e) => updateBillingTerms("frequency", e.target.value)}
                                            >
                                                <MenuItem value="Monthly">Monthly</MenuItem>
                                                <MenuItem value="Weekly">Weekly</MenuItem>
                                                <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                                                <MenuItem value="Quarterly">Quarterly</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="Overage"
                                                select
                                                fullWidth
                                                value={billingTerms.overage ? "true" : "false"}
                                                onChange={(e) => updateBillingTerms("overage", e.target.value === "true")}
                                            >
                                                <MenuItem value="true">Yes</MenuItem>
                                                <MenuItem value="false">No</MenuItem>
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                )}
                            </Section>
                        )}
                    </FormStack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
                    {saving ? <CircularProgress size={20} /> : "Save Client"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditBillingClientDialog;
